<template>
    <div class="wb-checkbox"
         :class="classes"
    >
        <label class="wb-checkbox__label">
            <input v-model="model"
                   class="wb-checkbox__native"
                   :name="name"
                   :required="required"
                   :value="value"
                   :disabled="disabled"
                   :checked="isChecked"
                   type="checkbox"
                   @change="onChange"
            />
            <span class="wb-checkbox__box">
                <WBIcon class="wb-checkbox__icon"
                        name="checkbox"
                />
            </span>

            <span v-if="slots.text"
                  class="wb-checkbox__slot"
            >
                <slot name="text" />
            </span>
        </label>
        <div class="wb-checkbox__error-message" />
    </div>
</template>

<script setup lang="ts">
    import type { WBCheckboxEmits, WBCheckboxProps } from '~/src/shared/components/ui/input/WBCheckbox/types'

    const props = withDefaults(defineProps<WBCheckboxProps>(), {
        modelValue: undefined,
        value: false,
        trueValue: true,
        falseValue: false,
        disabled: false,
        checked: false,
        error: ''
    })
    const emits = defineEmits<WBCheckboxEmits>()
    const slots = useSlots()

    const selfModel = ref(false)

    const isChecked = computed(() => {
        if (Array.isArray(props.modelValue)) {
            return props.modelValue.includes(props.value)
        }

        return props.modelValue === props.trueValue
    })

    const classes = computed(() => ({
        _checked: isChecked.value,
        _disabled: props.disabled,
        _error: props.error.length > 0,
    }))

    const model = computed({
        get() {
            return props.modelValue ?? selfModel.value
        },
        set(value) {
            emits('update:modelValue', value)
        },
    })

    const onChange = (event: Event) => {
        emits('change', (event.target as HTMLInputElement).checked)
    }

    onBeforeMount(() => {
        if (!props.checked) {
            return
        }

        if (Array.isArray(model.value) && !model.value.includes(props.value)) {
            model.value.push(props.value)
        } else {
            model.value = props.trueValue
        }
    })
</script>

<style lang="scss">
    .wb-checkbox {
        $checkbox: &;

        &__label {
            display: flex;
            align-items: center;
            cursor: pointer;
        }

        &__box {
            position: relative;
            flex-shrink: 0;
            width: 18px;
            height: 18px;
            margin-right: 8px;
            border-radius: 4px;
            border: 1px solid var(--blue-500);
        }

        &__icon {
            position: absolute;
            top: 50%;
            left: 50%;
            display: flex;
            align-items: center;
            width: 1.6rem;
            height: 1.6rem;
            color: var(--white);
            opacity: 0;
            transform: translate(-50%, -50%);
            transition: opacity 0.1s;
        }

        &__native {
            &:checked + #{$checkbox}__box {
                border-color: var(--blue-500);
                background-color: var(--blue-500);
            }

            &:checked + #{$checkbox}__box #{$checkbox}__icon {
                opacity: 1;
            }
        }

        &__message {
            position: absolute;
            top: calc(100% + 4px);
            left: 0;
            font-size: 12px;
            line-height: 1;
        }

        &._disabled {
            #{$checkbox}__label {
                color: var(--indigo-400);
                cursor: not-allowed;
            }

            #{$checkbox}__box {
                border-color: var(--indigo-100);
                background-color: var(--indigo-50);
            }
        }

        &._error {
            color: var(--error-color);

            #{$checkbox} {
                &__label {
                    color: var(--error-color);
                }

                &__native {
                    border-color: var(--error-color);
                }

                &__message {
                    color: var(--error-color);
                }
            }
        }
    }
</style>
